import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiClose } from '@mdi/js';

import './AccountMultiSelect.css';

const AccountMultiSelect = ({ accounts = [], value = [], onChange }) => {
    const { t } = useTranslation();
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selectedIds = useMemo(
        () => (Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : [])),
        [value]
    );
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const filteredAccounts = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return accounts;
        return accounts.filter((a) => (a.name || '').toLowerCase().includes(q));
    }, [accounts, query]);

    useEffect(() => {
        if (!open)
            return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const toggle = (id) => {
        if (selectedSet.has(id))
            onChange(selectedIds.filter((v) => v !== id));
        else
            onChange([...selectedIds, id]);
    };

    const selectAll = () => onChange(accounts.map((a) => a.id));
    const clearAll = () => onChange([]);

    const summary = (() => {
        const count = selectedIds.length;
        if (count === 0)
            return null;
        if (count === accounts.length && accounts.length > 0)
            return t('transactions.accounts_all_selected', { count });
        if (count > 2)
            return t('transactions.accounts_selected_count', { count });
        return selectedIds
            .map((id) => accounts.find((a) => a.id === id)?.name)
            .filter(Boolean)
            .join(', ');
    })();

    return (
        <div className="AccountMultiSelect" ref={rootRef}>
            <button
                type="button"
                className={`AccountMultiSelect__trigger${open ? ' is-open' : ''}`}
                onClick={() => setOpen((v) => !v)}
            >
                <span className={`AccountMultiSelect__summary${summary ? '' : ' is-placeholder'}`}>
                    {summary || t('transactions.accounts_placeholder')}
                </span>
                <span className="AccountMultiSelect__trigger-actions">
                    {selectedIds.length > 0 && (
                        <span
                            className="AccountMultiSelect__clear"
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => {
                                e.stopPropagation();
                                clearAll();
                            }}
                        >
                            <Icon path={mdiClose} size={0.7} />
                        </span>
                    )}
                    <Icon path={mdiChevronDown} size={0.8} color="#555" />
                </span>
            </button>

            {open && (
                <div className="AccountMultiSelect__menu">
                    <div className="AccountMultiSelect__search">
                        <Input
                            type="search"
                            bsSize="sm"
                            autoFocus
                            value={query}
                            placeholder={t('common.search')}
                            onChange={(e) => setQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    {accounts.length >= 5 && (
                        <div className="AccountMultiSelect__bulk">
                            <button type="button" onClick={selectAll}>{t('common.select_all')}</button>
                            <button type="button" onClick={clearAll}>{t('common.clear_selection')}</button>
                        </div>
                    )}
                    <div className="AccountMultiSelect__list">
                        {filteredAccounts.length === 0 && (
                            <div className="AccountMultiSelect__empty">
                                {t('transactions.accounts_no_results')}
                            </div>
                        )}
                        {filteredAccounts.map((account) => {
                            const checked = selectedSet.has(account.id);
                            return (
                                <label
                                    key={account.id}
                                    className={`AccountMultiSelect__option${checked ? ' is-checked' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(account.id)}
                                    />
                                    <span>{account.name}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountMultiSelect;
